import React, { useState } from 'react';
import { useEnv } from '../contexts/EnvContext';

interface DevToolsProps {
  // Optional props
}

const DevTools: React.FC<DevToolsProps> = () => {
  const { isDev, toggleDevMode, isActualDevEnvironment } = useEnv();
  const [isOpen, setIsOpen] = useState(false);

  // Toggle panel visibility
  const togglePanel = () => setIsOpen(!isOpen);

  // Don't render anything in production builds
  if (!isActualDevEnvironment) {
    return null;
  }

  return (
    <>
      {/* Always visible toggle button */}
      <button
        className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700 focus:outline-none"
        onClick={togglePanel}
        aria-label="Vis/skjul utviklerverktøy"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Dev tools panel */}
      {isOpen && (
        <div className="fixed bottom-16 right-4 z-50 bg-gray-900 text-white p-4 w-64 rounded-lg shadow-lg border border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Utviklerverktøy</h3>
            <button
              onClick={togglePanel}
              className="text-gray-400 hover:text-white"
              aria-label="Lukk utviklerverktøy"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* Environment Toggle */}
            <div className="flex flex-col">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="mr-3 text-sm font-medium">Miljø: {isDev ? 'Utvikling' : 'Produksjon'}</span>
                <div className="relative">
                  <input 
                    type="checkbox" 
                    className="sr-only" 
                    checked={isDev}
                    onChange={toggleDevMode}
                  />
                  <div className={`block w-10 h-6 rounded-full ${isDev ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${isDev ? 'transform translate-x-4' : ''}`}></div>
                </div>
              </label>
              <span className="text-xs text-gray-400 mt-1">
                {isDev ? 'Utviklingsmodus aktiv' : 'Produksjonsmodus aktiv'}
              </span>
            </div>
            
            {isDev && (
              <>
                <div className="border-t border-gray-700 pt-4">
                  <h4 className="text-sm font-medium mb-2">Debug-info</h4>
                  <div className="text-xs text-gray-400 space-y-1">
                    <p>React-versjon: {React.version}</p>
                    <p>Local Storage: {Object.keys(localStorage).length} elementer</p>
                    <p>Session Storage: {Object.keys(sessionStorage).length} elementer</p>
                    <p>Vindusstørrelse: {window.innerWidth}x{window.innerHeight}</p>
                  </div>
                </div>
                
                <div className="border-t border-gray-700 pt-4">
                  <h4 className="text-sm font-medium mb-2">Handlinger</h4>
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => localStorage.clear()}
                      className="text-xs bg-red-600 text-white py-1 px-2 rounded hover:bg-red-700"
                    >
                      Tøm Local Storage
                    </button>
                    <button
                      onClick={() => sessionStorage.clear()}
                      className="text-xs bg-red-600 text-white py-1 px-2 rounded hover:bg-red-700"
                    >
                      Tøm Session Storage
                    </button>
                    <button
                      onClick={() => window.location.reload()}
                      className="text-xs bg-blue-600 text-white py-1 px-2 rounded hover:bg-blue-700"
                    >
                      Last inn siden på nytt
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DevTools; 